import { call, put, takeLatest, takeLeading } from 'redux-saga/effects';
import { SendApiRequest } from 'src/api';
import { apiRoutes } from 'src/api/routes';
import {
    fetchCampaigns,
    campaignsFetched,
    campaignsFetchFailed,
    fetchCampaign,
    campaignFetched,
    campaignFetchFailed,
    fetchSummary,
    summaryFetched,
    summaryFetchFailed,
    sendCampaign,
    campaignSent,
    campaignSendFailed,
    runDryRun,
    dryRunCompleted,
    dryRunFailed,
    fetchUnsubscribes,
    unsubscribesFetched,
    unsubscribesFetchFailed,
    addUnsubscribe,
    removeUnsubscribe,
    cancelCampaign,
    duplicateCampaign,
    scheduleCampaign,
    testSendCampaign,
} from '../emailMarketing';
import { showToastAction } from '../ui';

function* fetchCampaignsEffect({ payload }) {
    try {
        const params = payload || {};
        const response = yield call(SendApiRequest, {
            url: apiRoutes.emailCampaigns.list,
            method: 'GET',
            queryParams: {
                page: params.page || 0,
                pageSize: params.pageSize || 20,
                status: params.status,
                startDate: params.startDate,
                endDate: params.endDate,
                search: params.search,
            },
        });
        yield put(campaignsFetched(response));
    } catch (error) {
        yield put(campaignsFetchFailed());
        yield put(
            showToastAction({
                type: 'error',
                message: error?.message || 'Failed to fetch campaigns',
            }),
        );
    }
}

function* fetchCampaignEffect({ payload }) {
    try {
        const response = yield call(SendApiRequest, {
            url: apiRoutes.emailCampaigns.detail(payload.id),
            method: 'GET',
            queryParams: {
                recipientPage: payload.recipientPage || 0,
                recipientPageSize: payload.recipientPageSize || 50,
            },
        });
        yield put(campaignFetched(response));
    } catch (error) {
        yield put(
            campaignFetchFailed({
                error: error?.message || 'Failed to load campaign',
            }),
        );
        yield put(
            showToastAction({
                type: 'error',
                message: error?.message || 'Failed to fetch campaign',
            }),
        );
    }
}

function* fetchSummaryEffect() {
    try {
        const response = yield call(SendApiRequest, {
            url: apiRoutes.emailCampaigns.summary,
            method: 'GET',
        });
        yield put(summaryFetched(response));
    } catch (error) {
        yield put(summaryFetchFailed());
    }
}

function* sendCampaignEffect({ payload }) {
    try {
        const response = yield call(SendApiRequest, {
            url: apiRoutes.emailCampaigns.send(payload.id),
            method: 'POST',
            body: payload.data || {},
        });
        yield put(campaignSent(response));
        yield put(
            showToastAction({
                type: 'success',
                message: response.message || 'Campaign queued for processing!',
            }),
        );
        if (payload.resolve) payload.resolve(response);
    } catch (error) {
        const msg =
            error?.response?.data?.error ||
            error?.message ||
            'Failed to send campaign';
        yield put(campaignSendFailed({ error: msg }));
        yield put(showToastAction({ type: 'error', message: msg }));
        if (payload.reject) payload.reject(error);
    }
}

function* runDryRunEffect({ payload }) {
    try {
        const response = yield call(SendApiRequest, {
            url: apiRoutes.emailCampaigns.dryRun,
            method: 'POST',
            body: payload,
        });
        yield put(dryRunCompleted(response));
    } catch (error) {
        yield put(dryRunFailed());
        yield put(
            showToastAction({
                type: 'error',
                message: error?.message || 'Dry run failed',
            }),
        );
    }
}

function* fetchUnsubscribesEffect({ payload }) {
    try {
        const params = payload || {};
        const response = yield call(SendApiRequest, {
            url: apiRoutes.emailUnsubscribes.list,
            method: 'GET',
            queryParams: {
                page: params.page || 0,
                pageSize: params.pageSize || 20,
                search: params.search,
            },
        });
        yield put(unsubscribesFetched(response));
    } catch (error) {
        yield put(unsubscribesFetchFailed());
    }
}

function* addUnsubscribeEffect({ payload }) {
    try {
        yield call(SendApiRequest, {
            url: apiRoutes.emailUnsubscribes.list,
            method: 'POST',
            body: { email: payload.email, reason: payload.reason },
        });
        yield put(
            showToastAction({
                type: 'success',
                message: 'Email added to unsubscribe list',
            }),
        );
        if (payload.resolve) payload.resolve();
    } catch (error) {
        yield put(
            showToastAction({
                type: 'error',
                message: error?.message || 'Failed to add unsubscribe',
            }),
        );
        if (payload.reject) payload.reject(error);
    }
}

function* removeUnsubscribeEffect({ payload }) {
    try {
        yield call(SendApiRequest, {
            url: apiRoutes.emailUnsubscribes.remove(payload.id),
            method: 'DELETE',
        });
        yield put(
            showToastAction({
                type: 'success',
                message: 'Email removed from unsubscribe list',
            }),
        );
        if (payload.resolve) payload.resolve();
    } catch (error) {
        yield put(
            showToastAction({
                type: 'error',
                message: error?.message || 'Failed to remove unsubscribe',
            }),
        );
    }
}

function* cancelCampaignEffect({ payload }) {
    try {
        yield call(SendApiRequest, {
            url: apiRoutes.emailCampaigns.cancel(payload.id),
            method: 'POST',
        });
        yield put(
            showToastAction({ type: 'success', message: 'Campaign cancelled' }),
        );
        if (payload.resolve) payload.resolve();
    } catch (error) {
        yield put(
            showToastAction({
                type: 'error',
                message: error?.message || 'Failed to cancel campaign',
            }),
        );
    }
}

function* duplicateCampaignEffect({ payload }) {
    try {
        const response = yield call(SendApiRequest, {
            url: apiRoutes.emailCampaigns.duplicate(payload.id),
            method: 'POST',
        });
        yield put(
            showToastAction({
                type: 'success',
                message: 'Campaign duplicated',
            }),
        );
        if (payload.resolve) payload.resolve(response);
    } catch (error) {
        yield put(
            showToastAction({
                type: 'error',
                message: error?.message || 'Failed to duplicate campaign',
            }),
        );
    }
}

function* scheduleCampaignEffect({ payload }) {
    try {
        yield call(SendApiRequest, {
            url: apiRoutes.emailCampaigns.schedule(payload.id),
            method: 'POST',
            body: { scheduledAt: payload.scheduledAt },
        });
        yield put(
            showToastAction({ type: 'success', message: 'Campaign scheduled' }),
        );
        if (payload.resolve) payload.resolve();
    } catch (error) {
        yield put(
            showToastAction({
                type: 'error',
                message: error?.message || 'Failed to schedule campaign',
            }),
        );
        if (payload.reject) payload.reject(error);
    }
}

function* testSendCampaignEffect({ payload }) {
    try {
        const response = yield call(SendApiRequest, {
            url: apiRoutes.emailCampaigns.testSend(payload.id),
            method: 'POST',
            body: { emails: payload.emails },
        });

        // The API returns 200 even when individual addresses are rejected by
        // the mail provider, so report the per-address failures instead of
        // claiming success.
        const results = response?.results || [];
        const failed = results.filter(r => r.status !== 'sent');
        if (failed.length) {
            const detail = failed
                .map(
                    r =>
                        `${r.email}: ${r.error || 'rejected by mail provider'}`,
                )
                .join(' | ');
            yield put(
                showToastAction({
                    type: 'error',
                    message: `Test send failed for ${failed.length} of ${results.length} address(es) — ${detail}`,
                }),
            );
        } else {
            yield put(
                showToastAction({
                    type: 'success',
                    message: 'Test emails sent!',
                }),
            );
        }
        if (payload.resolve) payload.resolve(response);
    } catch (error) {
        yield put(
            showToastAction({
                type: 'error',
                message: error?.message || 'Failed to send test emails',
            }),
        );
    }
}

export const emailMarketingSagas = [
    takeLatest(fetchCampaigns.type, fetchCampaignsEffect),
    takeLatest(fetchCampaign.type, fetchCampaignEffect),
    takeLatest(fetchSummary.type, fetchSummaryEffect),
    takeLeading(sendCampaign.type, sendCampaignEffect),
    takeLatest(runDryRun.type, runDryRunEffect),
    takeLatest(fetchUnsubscribes.type, fetchUnsubscribesEffect),
    takeLatest(addUnsubscribe.type, addUnsubscribeEffect),
    takeLatest(removeUnsubscribe.type, removeUnsubscribeEffect),
    takeLeading(cancelCampaign.type, cancelCampaignEffect),
    takeLatest(duplicateCampaign.type, duplicateCampaignEffect),
    takeLeading(scheduleCampaign.type, scheduleCampaignEffect),
    takeLatest(testSendCampaign.type, testSendCampaignEffect),
];
